import { _debugOnConsole } from "./utils";
import { VerificationPerformanceResults } from "./worker-interface";
import { WorkerExecutor } from "./worker";
import { XMLParser } from "fast-xml-parser";
import * as forge from "node-forge";
import { SignatureVerifySignatureResult, VerifySignatureResult } from "sign-pdf-lib";

declare const OTS_HASH_OID: string[];

const TSL_CACHE = new Map<string, any>();

/**
 * Função responsável pela verificação sigilosa, sem enviar o Documento ao back-end.
 * @param file_list Os PDFs a serem assinados.
 * @returns O resultado da verificação
 */
export async function private_verify(
  fileList: FileList,
  urls: string[],
  perf: VerificationPerformanceResults
): Promise<[SignatureVerifySignatureResult[], VerificationPerformanceResults]> {
  let begin = performance.now();
  let ogbegin = begin;
  const rootPromises = urls.map((url) => get_tsls(url));
  const roots = await Promise.all(rootPromises);
  let duplicatedRootCerts = roots.flat().sort();

  const rootCerts: string[] = [duplicatedRootCerts[0]];
  // O(n^2)...
  for (let i = 1; i < duplicatedRootCerts.length; ++i)
    if (duplicatedRootCerts[i] !== duplicatedRootCerts[i - 1]) {
      try {
        forge.pki.certificateFromPem(duplicatedRootCerts[i]);
        rootCerts.push(duplicatedRootCerts[i]);
      } catch (error) {
        console.error(`Certificate ${duplicatedRootCerts[i]} not compatible with node-forge. Reason: ${error}`);
      }
    }
  let end = performance.now();

  perf.tsls.value += end - begin;

  //const workerpool = pool(workerUrl.toString(), {
  //  minWorkers: 1,
  //  workerOpts: { type: "module" },
  //});
  const workerProxy = new WorkerExecutor();

  _debugOnConsole("Verificando %s PDFs...", fileList.length);

  let promises: Array<Promise<[SignatureVerifySignatureResult[], VerificationPerformanceResults]>> = [];
  let numSignedPdfs = 0;

  for (let i = 0; i < 1; ++i) {
    promises.push(
      fileList[i].arrayBuffer().then(async (buffer) => {
        let [results, perf2] = await workerProxy.verify(Buffer.from(buffer), rootCerts, OTS_HASH_OID, perf);

        return [results, perf2];
      })
    );
  }

  // Interface only supports one signature right now.
  const [results, perf2] = await promises[0];
  end = performance.now();
  perf2.total.value = end - ogbegin;
  perf2.tsls.value = perf.tsls.value;
  return [results, perf2];
}

function fetch_tsl(url: string): Promise<[string[], string[]]> {
  console.log(`fetching /api/v1/proxy?url=${encodeURIComponent(url)}...`);

  if (url.includes("https://sigilo.ades.labsec.ufsc.br")) {
    url = url.replace("https://sigilo.ades.labsec.ufsc.br", "http://localhost");
  }

  return fetch(`/api/v1/proxy?url=${encodeURIComponent(url)}`)
    .then(async (res) => {
      if (!res.ok) {
        console.error(res.status);
        return [[], []] as [string[], string[]];
      }
      const content = await res.text();
      const obj = new XMLParser({ removeNSPrefix: true }).parse(content);

      let tsl_pointers = obj.TrustServiceStatusList?.SchemeInformation?.PointersToOtherTSL?.OtherTSLPointer;

      if (!Array.isArray(tsl_pointers)) tsl_pointers = [tsl_pointers];

      let urls: string[] = [];
      let certs: string[] = [];
      for (const pointer of tsl_pointers) {
        if (pointer?.TSLLocation) urls.push(pointer.TSLLocation);
      }

      const wrap_in_arr = (obj: any) => (Array.isArray(obj) ? obj : [obj]);

      const tsps = wrap_in_arr(obj.TrustServiceStatusList?.TrustServiceProviderList?.TrustServiceProvider);

      for (const root of tsps) {
        if (!root) continue;
        const services = wrap_in_arr(root.TSPServices?.TSPService);

        for (const service of services) {
          if (!service) continue;

          const ids = wrap_in_arr(service.ServiceInformation?.ServiceDigitalIdentity?.DigitalId);

          for (const id of ids) {
            if (!id?.X509Certificate) continue;

            certs.push("-----BEGIN CERTIFICATE-----\n" + id.X509Certificate + "\n-----END CERTIFICATE-----");
          }
        }
      }

      return [urls, certs] as [string[], string[]];
    })
    .catch((e) => {
      console.error(`ERROR on fetch(${url}) => ${e}`);
      return [[], []] as [string[], string[]];
    });
}

async function get_tsls(url: string): Promise<Array<string>> {
  if (TSL_CACHE.has(url)) return TSL_CACHE.get(url);
  TSL_CACHE.set(url, []);
  if (/.*iti\.br.*/.test(url)) return [];

  let urls: Array<string> = [];
  let certs: Array<string> = [];

  [urls, certs] = await fetch_tsl(url);

  const promises: Array<Promise<string[]>> = [];
  for (const url2 of urls) {
    if (url2 !== url) promises.push(get_tsls(url2));
  }

  const certs2 = await Promise.all(promises);
  certs.concat(...certs2);

  TSL_CACHE.set(url, certs);

  return certs;
}
